import React from "react";
import { Plus, Flame, UtensilsCrossed } from "lucide-react";
import { money } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";

export function Badge({ type }) {
  if (type === "popular")
    return <span data-testid="badge-popular" className="bg-metros-black text-white text-[10px] px-2 py-0.5 rounded-md font-bold uppercase tracking-wider">Popular</span>;
  if (type === "new")
    return <span data-testid="badge-new" className="bg-metros-red text-white text-[10px] px-2 py-0.5 rounded-md font-bold uppercase tracking-wider">New</span>;
  if (type === "meal")
    return <span data-testid="badge-meal" className="bg-metros-red text-white text-[10px] px-2 py-0.5 rounded-md font-bold uppercase tracking-wider inline-flex items-center gap-1"><UtensilsCrossed size={10} /> Meal</span>;
  return null;
}

export default function ProductCard({ product, onOpen, compact = false, offerPrice = null }) {
  const soldOut = !product.available;
  const { settings } = useSettings();
  const mc = settings?.meal_deal || {};
  const mealEnabled = mc.enabled !== false;
  const mainCats = mc.main_categories || ["Burgers", "Chicken", "Kebabs", "Wraps", "Pizza"];
  const mainProducts = mc.main_products || [];
  const _cfg = (mc.item_config || {})[product.id] || {};
  const _allCust = mc.customizations || [];
  const _itemCats = (_cfg.customization_ids && _cfg.customization_ids.length) ? _allCust.filter((c) => _cfg.customization_ids.includes(c.id)) : _allCust;
  const mealEligible = mealEnabled && ((mainCats.includes(product.category_name)) || mainProducts.includes(product.id)) && _itemCats.some((cat) => (cat.items || []).some((it) => it.visible !== false && (it.name || "").trim()));
  return (
    <div
      data-testid={`product-card-${product.id}`}
      onClick={() => !soldOut && onOpen(product)}
      className={`group bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm transition-transform duration-300 ${soldOut ? "opacity-60" : "hover:-translate-y-1 hover:shadow-card cursor-pointer"}`}
    >
      <div className={`relative w-full overflow-hidden bg-gray-100 ${compact ? "h-24 sm:h-32 lg:h-44" : "h-44"}`}>
        <img src={product.image} alt={product.name} loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <div className="absolute top-2 left-2 flex gap-1.5">
          {product.popular && <Badge type="popular" />}
          {product.new && <Badge type="new" />}
          {mealEligible && <Badge type="meal" />}
        </div>
        {soldOut && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <span className="text-white font-bold uppercase tracking-wide text-xs border border-white/60 px-2 py-1 rounded-full">Sold Out</span>
          </div>
        )}
      </div>
      <div className={`flex flex-col ${compact ? "p-2 lg:p-4 gap-1" : "p-4 gap-1.5"}`}>
        <h3 className={`font-heading font-bold leading-tight text-metros-black ${compact ? "text-xs sm:text-sm lg:text-lg line-clamp-1" : "text-lg"}`}>{product.name}</h3>
        {!compact && <p className="text-sm text-muted-foreground line-clamp-2 min-h-[40px]">{product.description}</p>}
        {!compact && product.dietary?.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {product.dietary.map((d) => (
              <span key={d} className="text-[10px] font-semibold text-green-700 bg-green-50 px-1.5 py-0.5 rounded">{d}</span>
            ))}
          </div>
        )}
        <div className="flex justify-between items-center mt-1">
          {offerPrice != null && Number(offerPrice) >= 0 && Number(offerPrice) < product.price ? (
            <span className="flex items-baseline gap-1.5">
              <span className="line-through text-gray-400 text-xs sm:text-sm">{money(product.price)}</span>
              <span className={`font-heading font-extrabold text-metros-red ${compact ? "text-sm lg:text-lg" : "text-lg"}`}>{money(offerPrice)}</span>
            </span>
          ) : (
            <span className={`font-heading font-extrabold text-metros-black ${compact ? "text-sm lg:text-lg" : "text-lg"}`}>{money(product.price)}</span>
          )}
          <button
            data-testid={`quick-add-${product.id}`}
            disabled={soldOut}
            onClick={(e) => { e.stopPropagation(); onOpen(product); }}
            className={`bg-gray-100 hover:bg-metros-red hover:text-white text-metros-black rounded-full transition-colors active:scale-90 disabled:opacity-40 ${compact ? "p-1.5 lg:p-2.5" : "p-2.5"}`}
            aria-label={`Add ${product.name}`}
          >
            <Plus size={compact ? 15 : 18} />
          </button>
        </div>
      </div>
    </div>
  );
}
